package dev.lingvo.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.key
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.toAttrs
import dev.lingvo.shared.FileDto
import dev.lingvo.shared.FileFormat
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Async
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LDownloadIcon
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LSwitch
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.api.Session
import dev.lingvo.site.state.ProjectAuthoritySubject
import dev.lingvo.site.state.authoritySubject
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.util.downloadBlob
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.cssRem
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag

private const val ALL_LOCALES = "__all__"

/**
 * Taking strings out, on the page that brings them in.
 *
 * **This moved here to make `/import-export` deletable, and it was the thing blocking that.** The
 * plan was to redirect the old page at `/data` and delete it, on the grounds that two live pages
 * doing one job is how a bug gets fixed in the one nobody uses. The two pages were not doing one
 * job: `/data` implemented import-with-a-plan and then *linked back* to `/import-export?panel=export`
 * for export, which the old page still owned. Redirecting would have pointed that link at itself and
 * taken export off the product.
 *
 * Standalone callers load their own setup. Data shares one setup read with its import destination
 * controls, so an acknowledgement or retry updates both without duplicate file-list requests.
 */
@Composable
fun ExportCard(
    slug: String,
    requestedFileId: String? = null,
    requestedLocale: String? = null,
    requestedFormat: FileFormat? = null,
    refreshToken: Int = 0,
) {
    val subject = liveProjectRoute(slug)?.authoritySubject(Session.organizations)
    val setup = rememberExportSetup(slug, subject, refreshToken)
    ExportCard(slug, requestedFileId, requestedLocale, requestedFormat, setup)
}

@Composable
internal fun ExportCard(
    slug: String,
    requestedFileId: String?,
    requestedLocale: String?,
    requestedFormat: FileFormat?,
    setup: ExportSetupView,
) {
    val loaded = setup.loaded
    val state = loaded.state
    val stale = setup.stale
    val subject = setup.subject

    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            // Every other section heading on `/data` carries a Lucide mark; without one this
            // card's title starts 24px left of the four above it and reads as a different kind of
            // block. The glyph is the one already on the Download buttons inside the card.
            LSectionTitle(t("export_card.export", "Export"), icon = { LDownloadIcon() })
            val refused = ExportSetupFeedback(loaded, stale && state is Async.Success,
                onRetry = setup.retry, retryEnabled = setup.retryAllowed)
            when (state) {
                Async.Loading -> MutedCopy(t("export_card.loading_this_projects_files", "Loading this project's files…"))
                is Async.Failure -> Unit // The scoped refusal/retry above is the complete failure surface.
                is Async.Success -> if (!refused) key(slug, subject) {
                    ExportControls(slug, subject, state.value, requestedFileId, requestedLocale, requestedFormat, stale)
                }
            }
        }
    }
}

@Composable
private fun ExportControls(
    slug: String,
    subject: ProjectAuthoritySubject?,
    data: ExportData,
    requestedFileId: String?,
    requestedLocale: String?,
    requestedFormat: FileFormat?,
    stale: Boolean,
) {
    val scope = rememberCoroutineScope()
    val allLocales = (listOf(data.project.sourceLocale) + data.project.locales)
        .filter(String::isNotBlank)
        .distinct()
    val targetLocales = allLocales.filter { it != data.project.sourceLocale }

    var chosenFileId by remember(requestedFileId) {
        mutableStateOf(
            data.files.firstOrNull { it.id == requestedFileId }?.id
                ?: data.files.firstOrNull()?.id.orEmpty(),
        )
    }
    // **The deep link's whole point is that the card opens already answering it.** The readiness row
    // links here as "Export German" (`/data?locale=de`); a card that then opens on the first target
    // locale has silently changed which language the reader asked for, and the download button is
    // one press away from acting on that substitution. An unknown or misspelled code falls back to
    // the same default the card has always had rather than selecting nothing.
    var chosenLocale by remember(requestedLocale) {
        mutableStateOf(
            allLocales.firstOrNull { it.equals(requestedLocale, ignoreCase = true) }
                ?: targetLocales.firstOrNull()
                ?: data.project.sourceLocale,
        )
    }
    var formatId by remember(requestedFormat) { mutableStateOf(requestedFormat?.name.orEmpty()) }
    var fallback by remember { mutableStateOf(false) }
    var downloadingFile by remember { mutableStateOf(false) }
    var downloadingBundle by remember { mutableStateOf(false) }
    var failure by remember { mutableStateOf<String?>(null) }
    val fileId = chosenFileId.takeIf { chosen -> data.files.any { it.id == chosen } }
        ?: data.files.firstOrNull()?.id.orEmpty()
    val locale = chosenLocale.takeIf { it in allLocales || (it == ALL_LOCALES && targetLocales.isNotEmpty()) }
        ?: targetLocales.firstOrNull() ?: data.project.sourceLocale
    val file = data.files.firstOrNull { it.id == fileId }

    MutedCopy(t("export_card.download_one_file_or_every_locale", "Download one file, or every locale as a zip."))

    if (data.files.isEmpty()) {
        MutedCopy(if (stale) {
            t("export_card.last_list_empty", "The last loaded file list was empty. The current export setup has not been confirmed yet.")
        } else {
            t("export_card.there_is_nothing_to_export_yet", "There is nothing to export yet — import a source file first.")
        })
        return
    }

    // `live = true`, unlike every other alert in the shell: this one exists only because somebody
    // just pressed Download and nothing happened. A failure a screen-reader user has to go hunting
    // for is a failure they read as the button doing nothing at all.
    failure?.let {
        LAlert(
            t("export_card.that_download_didnt_start", "That download didn't start"),
            it,
            variant = LAlertVariant.WARNING,
            live = true,
        )
    }

    // **One width for the three selects, and one right edge.** Left to itself each field group
    // shrinks to its widest option, so a card whose every other element is flush and full width
    // stacked three boxes of three different widths — and at 390 the file group's intrinsic width
    // ran past the card, clipping the one value a reader has to verify before exporting.
    val selectWidth = Modifier.fillMaxWidth().maxWidth(26.cssRem)
    // The tail of the path, not the whole of it. `composeResources/values/strings.xml` is wider
    // than the control at 390 and a native select clips its value without an ellipsis, so the one
    // string a reader has to check before exporting was being cut mid-word. The last two segments
    // are what tell two files apart; the whole path is stated under the control when it was
    // shortened, so nothing is hidden.
    LSelect(
        fileId,
        data.files.map { LSelectOption(it.id, shortPath(it.path)) },
        { chosenFileId = it },
        selectWidth,
        label = t("export_card.file", "File"),
    )
    file?.path?.takeIf { it != shortPath(it) }?.let { MutedCopy(it) }
    val bundleOption = if (targetLocales.isNotEmpty()) {
        listOf(LSelectOption(ALL_LOCALES, t("export_card.all_target_locales", "All target locales")))
    } else {
        emptyList()
    }
    LSelect(
        locale,
        bundleOption + allLocales.map { code -> LSelectOption(code, localeLabel(code, data.project.sourceLocale)) },
        { chosenLocale = it },
        selectWidth,
        label = t("export_card.locale", "Locale"),
    )
    LSelect(
        formatId,
        listOf(LSelectOption("", t("export_card.keep_each_files_format", "Keep each file's format"))) +
            FileFormat.entries.map { LSelectOption(it.name, it.displayName) },
        { formatId = it },
        selectWidth,
        label = t("export_card.export_format", "Export format"),
    )
    LSwitch(
        fallback,
        { fallback = it },
        t("export_card.fallback_to_source_text", "Fallback to source text"),
        enabled = locale != ALL_LOCALES,
    )
    // Two controls die together on this one choice — the switch above and Download file below — and
    // said nothing about why. A control that greys out without a reason reads as a broken product,
    // and this is the shell component most likely to be read that way.
    if (locale == ALL_LOCALES) {
        MutedCopy(
            t(
                "export_card.every_locale_exports_as_a_bundle",
                "Every locale exports as a bundle. Pick a single locale to download one file, " +
                    "or to fall back to source text.",
            ),
        )
    }

    Row(Modifier.gap(Space.SM).flexWrap(FlexWrap.Wrap), verticalAlignment = Alignment.CenterVertically) {
        LButton(
            t("export_card.download_file", "Download file"),
            {
                val selected = file ?: return@LButton
                val claim = captureExportRead(slug, subject) ?: return@LButton
                val requested = formatId.takeIf(String::isNotBlank)?.let(FileFormat::valueOf)
                scope.launch {
                    downloadingFile = true
                    failure = null
                    try {
                        val text = requestExportRead(claim) { ApiClient.exportFile(slug, selected.id, locale, requested, fallback) }
                        downloadBlob(
                            Blob(arrayOf<dynamic>(text), BlobPropertyBag(type = "text/plain;charset=utf-8")),
                            exportName(slug, selected, locale, requested ?: selected.format),
                        )
                    } catch (cancelled: CancellationException) {
                        // **Rethrown, never reported.** Leaving the page cancels this scope, and a
                        // swallowed cancellation turned "you navigated away" into a warning saying
                        // the download had failed — on a download that was never attempted.
                        throw cancelled
                    } catch (error: ApiException) {
                        // The API's own sentence, which `ApiTransport` guarantees is a message the
                        // server deliberately wrote — never a raw body. "This file has no keys for
                        // ru" is worth more here than anything this component could invent.
                        failure = error.message.takeIf(String::isNotBlank) ?: downloadFileFailed()
                    } catch (error: Throwable) {
                        // Anything else is a fault inside the browser — a Blob the runtime refused,
                        // a format that would not parse — and its text is written for whoever
                        // debugs it, not for whoever is waiting on a file.
                        failure = downloadFileFailed()
                    } finally {
                        downloadingFile = false
                    }
                }
            },
            enabled = file != null && locale != ALL_LOCALES && !downloadingBundle,
            loading = downloadingFile,
            leadingIcon = { LDownloadIcon() },
        )
        LButton(
            t("export_card.download_bundle_zip", "Download bundle (zip)"),
            {
                val claim = captureExportRead(slug, subject) ?: return@LButton
                val requested = formatId.takeIf(String::isNotBlank)?.let(FileFormat::valueOf)
                scope.launch {
                    downloadingBundle = true
                    failure = null
                    try {
                        val locales = if (locale == ALL_LOCALES) emptyList() else listOf(locale)
                        downloadBlob(requestExportRead(claim) { ApiClient.exportBundle(slug, locales, requested) }, "$slug-translations.zip")
                    } catch (cancelled: CancellationException) {
                        throw cancelled
                    } catch (error: ApiException) {
                        failure = error.message.takeIf(String::isNotBlank) ?: downloadBundleFailed()
                    } catch (error: Throwable) {
                        failure = downloadBundleFailed()
                    } finally {
                        downloadingBundle = false
                    }
                }
            },
            variant = LButtonVariant.SECONDARY,
            enabled = !downloadingFile,
            loading = downloadingBundle,
            leadingIcon = { LDownloadIcon() },
        )
    }
}
